import React from 'react';

import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import styled from 'styled-components';

import Button from 'components/lib/Button';

import { TRANSCRIPT_REQUIREMENT_ACTION, TRANSCRIPT_REQUIREMENT_STATUS } from '../constants';
import { ShopRequestTranscriptRequirementAction, ShopRequestTranscriptRequirementType } from '../types';
import { getTranscriptRequirementActorName, getTranscriptRequirementUpdatedAtText } from '../utils';

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  box-sizing: border-box;
  width: 100%;
  border-radius: 8px;
  padding: 12px;

  &.confirmed {
    border: 1px solid var(--positive-green-border-light);
    background: var(--positive-green-background);
    color: var(--positive-green-dark);
  }

  &.rejected {
    border: 1px solid var(--error-red-border-light);
    background: var(--error-red-background);
    color: var(--error-red-dark);
  }

  &:hover,
  &:focus-within {
    .transcript-requirement-clear-action:not(:disabled):not(.ant-btn-disabled) {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .transcript-requirement-clear-action.is-loading {
    opacity: 1;
    pointer-events: none;
  }
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Text = styled.span`
  display: block;
  font-size: 12px;
  line-height: normal;

  &.confirmed {
    color: var(--positive-green-dark);
  }

  &.rejected {
    color: var(--error-red-dark);
  }
`;

const StrongText = styled.span`
  font-family: var(--font-bold);
`;

const Summary = styled.span`
  display: block;
`;

const RejectionReason = styled.span`
  display: block;
  margin-top: 4px;
`;

const ClearButton = styled(Button)`
  flex-shrink: 0;
  color: var(--dark-blue);
  padding: 0;
  opacity: 0;
  pointer-events: none;
  box-shadow: none;
`;

type Props = {
  requirement: ShopRequestTranscriptRequirementType;
  activeAction: ShopRequestTranscriptRequirementAction | null;
  isSubmitting: boolean;
  onClear: () => void;
};

const EditShopRequestModalTranscriptRequirementResolution = ({
  requirement,
  activeAction,
  isSubmitting,
  onClear,
}: Props) => {
  const isConfirmed = requirement.status === TRANSCRIPT_REQUIREMENT_STATUS.CONFIRMED;
  const isClearing = activeAction === TRANSCRIPT_REQUIREMENT_ACTION.CLEAR;

  return (
    <Container className={requirement.status}>
      <Content>
        <IconContainer>{isConfirmed ? <CheckCircleOutlined /> : <CloseCircleOutlined />}</IconContainer>
        <Text className={requirement.status}>
          <Summary>
            <StrongText>{isConfirmed ? 'Transcript confirmed' : 'Transcript not provided'}</StrongText> by{' '}
            {getTranscriptRequirementActorName(requirement)}
            {getTranscriptRequirementUpdatedAtText(requirement)}
          </Summary>
          {requirement.status === TRANSCRIPT_REQUIREMENT_STATUS.REJECTED && requirement.rejection_reason && (
            <RejectionReason>Reason: {requirement.rejection_reason}</RejectionReason>
          )}
        </Text>
      </Content>
      <ClearButton
        className={classNames('transcript-requirement-clear-action', { 'is-loading': isClearing })}
        size="small"
        type="link"
        disabled={isSubmitting}
        loading={isClearing}
        onClick={!isSubmitting ? onClear : undefined}
      >
        Clear
      </ClearButton>
    </Container>
  );
};

export default EditShopRequestModalTranscriptRequirementResolution;
